// Attempt selection must not depend on Firestore's document order.
export const attemptTime = value => {
    if (value?.toMillis) return value.toMillis();
    if (value?.toDate) return value.toDate().getTime();
    if (value && typeof value === 'object' && Number.isFinite(value.seconds)) return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
};
export const isSubmittedAttempt = attempt => Boolean(attempt.submittedAt) || attempt.submissionState === 'submitted';
const newest = (a, b) => attemptTime(b.startedAt) - attemptTime(a.startedAt) || String(b.id).localeCompare(String(a.id));
export function selectExamAttempt(attempts = []) {
    const ordered = [...attempts].sort(newest);
    const completed = ordered.filter(isSubmittedAttempt);
    const approved = completed.find(attempt => attempt.allowRetake === true);
    if (approved) return { kind: 'retake', previous: approved };
    const latestCompleted = completed[0];
    const active = ordered.find(attempt => !isSubmittedAttempt(attempt) && !attempt.retaken &&
        (!latestCompleted || attempt.retakeOf === latestCompleted.id ||
        attemptTime(attempt.startedAt) > Math.max(attemptTime(latestCompleted.startedAt), attemptTime(latestCompleted.submittedAt))));
    if (active) return { kind: 'resume', attempt: active };
    return latestCompleted ? { kind: 'blocked', attempt: latestCompleted } : { kind: 'create' };
}
export function flattenExamQuestions(questions = []) {
    return questions.flatMap((q, originalIndex) => q.type === 'case-study'
        ? (q.subQuestions || []).map((sq, subIndex) => ({ ...sq, id: `${originalIndex}-${subIndex}`, originalIndex, subIndex,
            section: q.section, scenarioText: q.text, scenarioImages: q.images, isCaseStudyChild: true, parentType: 'case-study' }))
        : [{ ...q, id: String(originalIndex), originalIndex }]);
}
export function flattenExamAnswers(answers = {}, questions = []) {
    const flat = {};
    Object.entries(answers).forEach(([key, value]) => {
        if (questions[key]?.type === 'case-study' && value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([sub, answer]) => { flat[`${key}-${sub}`] = answer; });
        } else flat[key] = value;
    });
    return flat;
}
export function attemptEndTime(attempt, durationMinutes, managedSession) {
    const started = managedSession?.startedAtMs || attemptTime(attempt.startedAt);
    const duration = managedSession?.durationMs ?? Number(attempt.durationMinutes ?? durationMinutes) * 60000;
    if (!Number.isFinite(started) || started <= 0 || !Number.isFinite(duration) || duration <= 0) {
        throw new Error('The exam start time or duration is missing. Please contact your coordinator.');
    }
    return started + duration;
}
// React StrictMode and repeated auth/provider renders must share one start operation.
const starting = new Map();
export function startAttemptOnce(key, operation) {
    if (!starting.has(key)) {
        const pending = Promise.resolve().then(operation);
        starting.set(key, pending);
        pending.finally(() => { if (starting.get(key) === pending) starting.delete(key); }).catch(() => {});
    }
    return starting.get(key);
}
export function withExamTimeout(promise, milliseconds = 30000) {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('The connection is taking too long. Your submission can be retried.')), milliseconds);
    })]).finally(() => clearTimeout(timer));
}
